import React from 'react';
import { FileText, FileSpreadsheet, FileType, Loader2, CheckCircle2, AlertCircle, Download, ExternalLink } from 'lucide-react';

/**
 * ToolCallBubble — affiche dans le chat un appel d'outil de génération
 * de document décidé par Adha (style ChatGPT/Claude function calling).
 *
 * Cycle :
 *   1. `started`   — bulle bleue avec spinner « Génération du Bilan… »
 *   2. `completed` — bulle verte avec icône fichier + lien cliquable + bouton télécharger
 *   3. `error`     — bulle rouge avec message d'erreur
 *
 * Le composant ne pilote pas son cycle de vie — il rend une `entry` du
 * store `useChatStore.toolCalls[]`. Le store met à jour `status` quand
 * l'event Socket.IO `adha.stream.tool` (type=tool_result) arrive.
 */

export interface ToolCallEntry {
  id: string;
  tool: string;
  status: 'started' | 'completed' | 'error';
  args?: Record<string, unknown>;
  url?: string;
  downloadUrl?: string;
  filename?: string;
  format?: string;
  sizeKb?: number;
  error?: string;
}

interface ToolCallBubbleProps {
  entry: ToolCallEntry;
}

/**
 * Mapping nom-tool → libellé humain pour l'affichage.
 * Si le LLM ajoute un nouveau tool, on tombe sur le fallback générique
 * sans casser l'UI.
 */
const TOOL_LABEL: Record<string, string> = {
  generate_financial_statement: 'État financier',
  export_journal: 'Export comptable',
  generate_tax_declaration: 'Déclaration fiscale',
  generate_custom_report: 'Rapport',
};

const STATEMENT_SUBLABEL: Record<string, string> = {
  balance_sheet: 'Bilan SYSCOHADA',
  income_statement: 'Compte de résultat',
  cash_flow: 'Flux de trésorerie',
  tafire: 'TAFIRE',
  trial_balance: 'Balance générale',
};

function FormatIcon({ format }: { format?: string }) {
  const cls = 'w-4 h-4 flex-shrink-0';
  switch ((format || '').toLowerCase()) {
    case 'xlsx':
    case 'csv':
      return <FileSpreadsheet className={`${cls} text-emerald-600`} />;
    case 'pdf':
      return <FileText className={`${cls} text-red-600`} />;
    case 'docx':
      return <FileType className={`${cls} text-blue-600`} />;
    default:
      return <FileText className={`${cls} text-muted-foreground`} />;
  }
}

function getDisplayLabel(entry: ToolCallEntry): string {
  const base = TOOL_LABEL[entry.tool] ?? entry.tool;
  // Sous-libellé contextuel selon les args
  if (entry.tool === 'generate_financial_statement' && entry.args) {
    const st = (entry.args as { statement_type?: string }).statement_type;
    if (st && STATEMENT_SUBLABEL[st]) return STATEMENT_SUBLABEL[st];
  }
  if (entry.tool === 'generate_tax_declaration' && entry.args) {
    const dt = (entry.args as { declaration_type?: string; period?: string }).declaration_type;
    const pd = (entry.args as { declaration_type?: string; period?: string }).period;
    if (dt) return `Déclaration ${dt}${pd ? ` — ${pd}` : ''}`;
  }
  if (entry.tool === 'export_journal' && entry.args) {
    const jt = (entry.args as { journal_type?: string; period?: string }).journal_type;
    const pd = (entry.args as { journal_type?: string; period?: string }).period;
    return `Export ${jt || 'journal'}${pd ? ` — ${pd}` : ''}`;
  }
  return base;
}

export function ToolCallBubble({ entry }: ToolCallBubbleProps) {
  const label = getDisplayLabel(entry);

  if (entry.status === 'started') {
    return (
      <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-sm">
        <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="text-blue-700 dark:text-blue-300 font-medium">
          Génération du document : {label}
        </span>
        <span className="text-xs text-blue-500/70 dark:text-blue-400/70 ml-auto">
          en cours…
        </span>
      </div>
    );
  }

  if (entry.status === 'error') {
    return (
      <div className="mb-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
        <div className="flex-1 min-w-0">
          <div className="text-red-700 dark:text-red-300 font-medium">
            Échec de génération : {label}
          </div>
          {entry.error && (
            <div className="text-xs text-red-600/80 dark:text-red-400/80 mt-0.5 break-words">
              {entry.error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // status === 'completed'
  const url = entry.url || entry.downloadUrl;
  return (
    <div className="mb-2 flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-sm">
      <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
      <FormatIcon format={entry.format} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-emerald-900 dark:text-emerald-100 truncate">
          {label}
        </div>
        <div className="text-[11px] text-emerald-700/70 dark:text-emerald-300/70 truncate">
          {entry.filename}
          {typeof entry.sizeKb === 'number' && ` · ${entry.sizeKb} Ko`}
          {entry.format && ` · ${entry.format.toUpperCase()}`}
        </div>
      </div>
      {url && (
        <div className="flex items-center gap-1">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
            title="Ouvrir dans un nouvel onglet"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={entry.downloadUrl || url}
            download={entry.filename}
            className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
            title="Télécharger"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}
